import { useRef } from "react";
import { api } from "../lib/api";

/**
 * Plays translated text via OpenAI TTS, with a browser-TTS fallback when the
 * network call fails. Returns { speak, stop, audioRef } so the caller can
 * also pause playback (e.g. before starting a new turn).
 */
const BROWSER_LANG_MAP = {
  Spanish: "es-ES", Mandarin: "zh-CN", French: "fr-FR", German: "de-DE",
  Japanese: "ja-JP", Korean: "ko-KR", Russian: "ru-RU", Portuguese: "pt-PT",
  Hindi: "hi-IN", Arabic: "ar-SA", Vietnamese: "vi-VN", English: "en-US",
};

export function useTTSPlayback(voice = "nova") {
  const audioRef = useRef(null);

  const stop = () => {
    try {
      audioRef.current?.pause();
    } catch {
      /* ignore */
    }
  };

  const speak = async (text, targetLang) => {
    if (!text) return;
    try {
      const res = await api.post(
        "/tts",
        { text, voice, speed: 0.95 },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch {
      // Fallback to browser TTS
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        const utter = new SpeechSynthesisUtterance(text);
        utter.lang = BROWSER_LANG_MAP[targetLang] || "en-US";
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utter);
      }
    }
  };

  return { speak, stop, audioRef };
}
